import * as m from '@staiter/i18n';
import {
  PROJECT_HEALTH_LABEL,
  PROJECT_STAGE_LABEL,
  type Project,
  type ProjectHealth,
  type ProjectStage,
  QueryBool,
} from '@staiter/shared';
import type { FilterRule } from '@staiter/shared/task-module';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import {
  AlertTriangle,
  Building2,
  Eye,
  EyeOff,
  Filter,
  FilterX,
  Grid3x3,
  List as ListIcon,
  MoreHorizontal,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { EmptyState, Table, type TableColumn } from '~/components/data';
import { ColumnConfigPopover, type ColumnDescriptor } from '~/components/data/ColumnConfigPopover';
import { ScopeAllCompaniesCta } from '~/components/domain/ScopeAllCompaniesCta';
import { ProjectCard } from '~/components/domain/project/ProjectCard';
import { ProjectDisplayPanel } from '~/components/domain/project/ProjectDisplayPanel';
import { Button, Input, Select } from '~/components/primitives';
import {
  openContextMenuFromButton,
  openContextMenuFromEvent,
} from '~/components/primitives/ContextMenu';
import { StatusBadge } from '~/components/status';
import toolbar from '~/components/tables/table-toolbar.module.css';
import { fmtDate } from '~/components/tasks/columns/cells';
import { ColumnFilterPopover } from '~/components/tasks/filters/ColumnFilterPopover';
import { FilterCard } from '~/components/tasks/filters/FilterCard';
import { TaskPopover } from '~/components/tasks/inline-editors/TaskPopover';
import { WorkspaceSurface } from '~/components/tasks/views/WorkspaceSurface';
import { apiRequest } from '~/lib/api/client';
import { fetchProjects } from '~/lib/api/projects';
import { formatMoney } from '~/lib/format/money';
import { healthBadge, healthChipVisible, stageBadge } from '~/lib/projects/badge-tone';
import { buildProjectFilterFields } from '~/lib/projects/filter-fields';
import { projectRulesToQuery, queryToProjectRules } from '~/lib/projects/rules';
import { terminalLast } from '~/lib/projects/terminal-last';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import { type ProjectMenuTarget, useProjectActions } from '~/lib/tasks/actions/use-project-actions';
import { PROJECT_STATUS_LABEL } from '~/lib/tasks/i18n/labels';
import { useProjectsViewStore } from '~/lib/tasks/stores/projectsViewStore';
import { useWorkTasksViewStore } from '~/lib/tasks/stores/workTasksViewStore';
import { useActiveEntityStore } from '~/store/active-entity';
import {
  PROJECTS_PAGE_SIZE_OPTIONS,
  type ProjectsTableSort,
  useProjectsTableStore,
} from '~/store/projectsTable';
import shared from './_auth._shared.module.css';
import styles from './_auth.projects.index.module.css';

// FE-18 — карты `HEALTH_TO_BADGE`/`STAGE_TO_BADGE` уехали в `lib/projects/badge-tone`:
// вторая копия жила в `ProjectCard.tsx`, и виды успели разойтись (дизайн-гейт фазы A).

/**
 * Гейт фаз C/D — признак архивной строки.
 *
 * Легаси-схема `Project` этого экрана поля `archived_at` не объявляет, но сервер
 * его отдаёт (`mapProjectRow`), и с включённым тумблером архивные приходят в общей
 * выдаче. Читаем его через узкое сужение, а не догадкой по стадии: догадка и была
 * причиной, по которой архивный проект выглядел живым.
 */
function isArchivedRow(p: Project): boolean {
  const v = (p as unknown as { archived_at?: unknown }).archived_at;
  return typeof v === 'string' && v.length > 0;
}

/** Строка списка → цель меню: реестру нужны стадия, статус, видимость и архив. */
function toProjectMenuTarget(p: Project): ProjectMenuTarget {
  return {
    id: p.id,
    name: p.name,
    code: p.code,
    stage: p.stage,
    status: p.status,
    health: p.health,
    visibility: p.visibility,
    // F9/F10 — признак архива теперь читается из ответа: с тумблером архивные
    // приходят в общей выдаче, и без него пункт «Вернуть из архива…» не появился
    // бы там, где он единственно и нужен.
    archived_at: ((p as unknown as { archived_at?: unknown }).archived_at as string | null) ?? null,
    // F2 (tier2-approval-requests §6) — активная заявка: пункт повторного запуска
    // того же действия выключается С ПРИЧИНОЙ, а не отвечает 409 после клика.
    pending_approval:
      (p as unknown as { pending_approval?: { id: string; task_type: string } | null })
        .pending_approval ?? null,
  };
}

/** G4 — Project list (URL state filters + cards/table toggle). */
const projectsSearchSchema = z.object({
  // F6 — релаксированы enum→string: колоночные воронки (ColumnFilterPopover) пишут CSV
  // мультивыбор (in_list), который BE принимает через B1 inArray. Бакеты пишут single.
  stage: z.string().optional(),
  health: z.string().optional(),
  // F1 (project-actions-parity) — исход проекта: вторая ось рядом со стадией. CSV, как
  // stage/health; единственное значение, которого стадия выразить не может, — `cancelled`.
  status: z.string().optional(),
  customer_id: z.string().optional(),
  pm_user_id: z.string().uuid().optional().catch(undefined),
  // project-visibility (F6) — фильтр по видимости (CSV).
  visibility: z.string().optional(),
  // projects-by-company P4 — фильтр по компании (агрегат, CSV).
  entity_id: z.string().optional(),
  // F5 — rule-builder операторы в URL (шэрабл): исключения + диапазон срока + пусто.
  stage_not: z.string().optional(),
  health_not: z.string().optional(),
  expected_end_from: z.string().optional(),
  expected_end_to: z.string().optional(),
  end_date_empty: QueryBool.optional().catch(undefined),
  // projects-by-company P4 — группировка по компании (агрегат): секции по entity.
  group: z.enum(['entity']).optional().catch(undefined),
  q: z.string().optional(),
  view: z.enum(['cards', 'table']).optional().catch(undefined),
  /**
   * F10 (project-actions-parity §8.2-О3) — «Показывать архивные».
   *
   * Параметр `include_archived` жил на сервере с самого начала
   * (`task-module-projects.ts:294` — приём, `:429` — применение), и вызывающего у
   * него не было НИ ОДНОГО: архивные проекты были недостижимы из списка в принципе,
   * только прямой ссылкой. Ровно класс «ручка есть, вызывающего нет».
   */
  include_archived: QueryBool.optional().catch(undefined),
});
// S12 — без .strict(): неизвестные query-параметры стрипаются, а не роняют app.

export const Route = createFileRoute('/_auth/projects/')({
  staticData: { minMainWidth: 960 },
  validateSearch: projectsSearchSchema,
  component: ProjectsPage,
});

function ProjectsPage(): JSX.Element {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/projects/' });
  const scope = useUserScope();
  const canCreate = canDo(scope, 'project', 'update');
  const stage = search.stage ?? '';
  const health = search.health ?? '';
  const status = search.status ?? '';
  const includeArchived = search.include_archived === true;
  const q = search.q ?? '';
  // F1 — режим вида: URL `?view=` (временный override для шэрабл-ссылок) поверх
  // запомненного в сторе. Заход без параметра открывает последний кликнутый режим.
  const storedViewMode = useProjectsViewStore((s) => s.viewMode);
  const setStoredViewMode = useProjectsViewStore((s) => s.setViewMode);
  const view = search.view ?? storedViewMode;
  const customerId = search.customer_id ?? '';
  const entityId = search.entity_id ?? '';
  const groupByCompany = search.group === 'entity';
  const setSearch = (next: Partial<z.infer<typeof projectsSearchSchema>>): void => {
    const merged: Record<string, unknown> = {};
    const current = {
      stage,
      health,
      // F1 — исход переносится между навигациями наравне с остальными правилами:
      // без этого любой setSearch-клик стирал бы активный фильтр «Отменённые».
      status,
      // F10 — тумблер архивных переживает любой другой клик по фильтрам.
      include_archived: search.include_archived,
      q,
      view,
      customer_id: customerId,
      entity_id: entityId,
      group: search.group,
      // F4 — visibility ДОЛЖЕН быть в current, иначе любой другой setSearch-клик стирал
      // бы активный visibility-фильтр (он не переносился между навигациями). Скептик #15.
      visibility: search.visibility,
      // F5 — rule-builder параметры тоже переносятся между навигациями (иначе setQ/бакет
      // стирал бы активные правила исключения/срока).
      stage_not: search.stage_not,
      health_not: search.health_not,
      expected_end_from: search.expected_end_from,
      expected_end_to: search.expected_end_to,
      end_date_empty: search.end_date_empty,
    };
    for (const [k, v] of Object.entries({ ...current, ...next })) {
      if (v !== '' && v !== undefined && v !== null) merged[k] = v;
    }
    navigate({ to: '/projects', search: merged as never });
  };
  const setStage = (s: ProjectStage | '') => setSearch({ stage: s || undefined });
  const setHealth = (h: ProjectHealth | '') => setSearch({ health: h || undefined });
  const setStatus = (s: string) => setSearch({ status: s || undefined });
  const setIncludeArchived = (next: boolean) =>
    setSearch({ include_archived: next ? true : undefined });
  const setQ = (next: string) => setSearch({ q: next || undefined });
  // F1 — клик тумблера пишет И URL, И стор (стор = запомненный дефолт на будущие заходы).
  const setView = (next: 'cards' | 'table') => {
    setStoredViewMode(next);
    setSearch({ view: next });
  };
  const setCustomerId = (id: string) => setSearch({ customer_id: id || undefined });
  const setEntityId = (id: string) => setSearch({ entity_id: id || undefined });
  const toggleGroupByCompany = () => setSearch({ group: groupByCompany ? undefined : 'entity' });

  // FE-88 (Sprint 11) — Mobile force-Cards view: <720px viewport disables Table mode
  // (плотная таблица на mobile теряет smysl — STAITER_UX «Mobile = view-only»).
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 719px)');
    const handle = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile(e.matches);
    handle(mq);
    mq.addEventListener('change', handle as (e: MediaQueryListEvent) => void);
    return () => mq.removeEventListener('change', handle as (e: MediaQueryListEvent) => void);
  }, []);
  const effectiveView: 'cards' | 'table' = isMobile ? 'cards' : view;
  const visibility = search.visibility;
  // F3 (D9) — «Показывать завершённые» (общий бит с задачами, §7-L11). Скрыть
  // stage∈{completed,closed} по умолчанию; guard: явный выбор стадии (бакет/воронка)
  // переопределяет hide (иначе бакет «Закрытые» недостижим, скептик-UX #8,#9).
  const showClosed = useWorkTasksViewStore((s) => s.showClosed);
  const setShowClosed = useWorkTasksViewStore((s) => s.setShowClosed);
  const userPickedStage = stage !== '';
  const hideClosed = !showClosed && !userPickedStage;
  /**
   * D8.2, раунд 3 гейта — активна ли ещё какая-нибудь причина сокращения выдачи,
   * кроме скрытия завершённых. От этого зависит, называть причину точно или
   * обобщённо: указатель, ведущий не к тому органу управления, дороже обобщения.
   */
  /**
   * Раунд 4 гейта захода 6 (UX, 🔴 BLOCKER): «MAJOR-1 не подтверждается кадром —
   * оговорка дословно читается как „… 24 скрыты фильтрами“, при этом сузил
   * результат поиск „проект“, а рядом нет обещанного действия „очистить
   * поиск“». Ревьюер прав, и причина именно та, о которой он предупредил
   * («либо правка не доехала»): поиск ВХОДИЛ в `hasOtherFilters`, а новая
   * ветка требовала `!hasOtherFilters` — то есть не срабатывала никогда, ровно
   * в том случае, ради которого писалась.
   *
   * Поэтому причин теперь две переменные, а не одна: «сузило что-то, кроме
   * поиска» и «сузил поиск». Первая решает, называть ли причину точно;
   * вторая — какую именно называть.
   */
  const hasNonQueryFilters =
    stage !== '' ||
    health !== '' ||
    status !== '' ||
    customerId !== '' ||
    entityId !== '' ||
    (search.visibility ?? '') !== '' ||
    (search.stage_not ?? '') !== '' ||
    (search.health_not ?? '') !== '' ||
    (search.expected_end_from ?? '') !== '' ||
    (search.expected_end_to ?? '') !== '' ||
    search.end_date_empty === true;
  // F15: выдача сужена поиском или фильтром — счётчики чипов тогда отвечают на
  // другой вопрос, чем строки, и это надо показать.
  const narrowed = q !== '' || hasNonQueryFilters;
  // F15 круг 3 (обе половины, MINOR): пустоту сузил ТОЛЬКО поиск — тогда и текст,
  // и действие говорят о поиске, а не о фильтрах.
  const searchOnly = q !== '' && !hasNonQueryFilters && !stage && !health && !status;
  const hasOtherFilters =
    stage !== '' ||
    health !== '' ||
    status !== '' ||
    q !== '' ||
    customerId !== '' ||
    entityId !== '' ||
    (search.visibility ?? '') !== '' ||
    (search.stage_not ?? '') !== '' ||
    (search.health_not ?? '') !== '' ||
    (search.expected_end_from ?? '') !== '' ||
    (search.expected_end_to ?? '') !== '' ||
    search.end_date_empty === true;
  // F5 — rule-builder параметры из URL + слияние stage_not (правило исключения ∪ hideClosed).
  const healthNot = search.health_not ?? '';
  const endFrom = search.expected_end_from ?? '';
  const endTo = search.expected_end_to ?? '';
  const endEmpty = search.end_date_empty === true;
  const stageNot = [search.stage_not, hideClosed ? 'completed,closed' : '']
    .filter(Boolean)
    .join(',');
  // F2/F3 — конфиг табличного вида (ширины/скрытые/порядок/сортировка/размер страницы).
  const tableSort = useProjectsTableStore((s) => s.sort);
  const setTableSort = useProjectsTableStore((s) => s.setSort);
  const tableWidths = useProjectsTableStore((s) => s.widths);
  const setTableWidth = useProjectsTableStore((s) => s.setWidth);
  const tableHidden = useProjectsTableStore((s) => s.hidden);
  const toggleTableHidden = useProjectsTableStore((s) => s.toggleHidden);
  const tableOrder = useProjectsTableStore((s) => s.order);
  const setTableOrder = useProjectsTableStore((s) => s.setOrder);
  const resetTableColumns = useProjectsTableStore((s) => s.resetColumns);
  const pageSize = useProjectsTableStore((s) => s.pageSize);
  const setPageSize = useProjectsTableStore((s) => s.setPageSize);
  const query = useQuery({
    // F3 — sort/order/pageSize в ключе: иначе React Query отдаст закэшированные
    // несортированные данные, серверный порядок не применится (скептик #8).
    queryKey: [
      'projects',
      {
        stage,
        health,
        status,
        includeArchived,
        q,
        customerId,
        entityId,
        visibility,
        tableSort,
        pageSize,
        hideClosed,
        stageNot,
        healthNot,
        endFrom,
        endTo,
        endEmpty,
      },
    ],
    queryFn: () =>
      fetchProjects({
        ...(stage ? { stage } : {}),
        ...(health ? { health } : {}),
        ...(status ? { status } : {}),
        ...(includeArchived ? { include_archived: true } : {}),
        ...(q ? { q } : {}),
        ...(customerId ? { customer_id: customerId } : {}),
        ...(entityId ? { entity_id: entityId } : {}),
        ...(visibility ? { visibility } : {}),
        ...(tableSort ? { sort: tableSort.id, order: tableSort.dir } : {}),
        // F3/D9 + F5 — исключение стадий: hideClosed ∪ rule-builder not_in (B1 stage_not).
        ...(stageNot ? { stage_not: stageNot } : {}),
        ...(healthNot ? { health_not: healthNot } : {}),
        ...(endFrom ? { expected_end_from: endFrom } : {}),
        ...(endTo ? { expected_end_to: endTo } : {}),
        ...(endEmpty ? { end_date_empty: true } : {}),
        limit: pageSize,
      }),
  });
  const rows = query.data?.items ?? [];
  const totalCount = query.data?.total_count ?? rows.length;
  // D3.6a (Z-F13) — сколько записей не прошло схему на последнем разборе.
  const droppedRows = query.data?.dropped_rows ?? 0;
  const droppedTraceId = query.data?.dropped_trace_id ?? null;
  // projects-by-company P2 — имена компаний (denorm) + колонка/чип «Компания» только в
  // мультикомпанийном охвате (holding/all). В single-entity всё — активная компания.
  const entities = query.data?.entities ?? {};
  const activeScope = useActiveEntityStore((s) => s.activeScope);
  // F14 — меню проекта. Перенос между компаниями гейтит СЕРВЕР по членству
  // (решение владельца 2026-08-01), поэтому признак — «компаний в доступе > 1»,
  // а не роль.
  const buildProjectMenu = useProjectActions({
    surface: 'list',
    canMoveEntity: Object.keys(entities).length > 1,
  });
  const showEntityColumn = activeScope !== 'entity';
  const entityName = (id: string): string => entities[id]?.name ?? id.slice(0, 8);
  // projects-by-company P4 — секции по компании (агрегат + group=entity), сортировка по имени.
  const groupedByEntity = useMemo(() => {
    const nameOf = (id: string): string => entities[id]?.name ?? id.slice(0, 8);
    const map = new Map<string, Project[]>();
    for (const p of rows) {
      const arr = map.get(p.entity_id) ?? [];
      arr.push(p);
      map.set(p.entity_id, arr);
    }
    return [...map.entries()].sort((a, b) => nameOf(a[0]).localeCompare(nameOf(b[0]), 'ru'));
  }, [rows, entities]);

  // list-FE UX-1 (CRITICAL): Hero Metrics — use BE-PROJ-1 /metrics endpoint
  // (portfolio-wide aggregate, scales к 1000+ projects). Fallback на rows.
  const metricsQuery = useQuery({
    queryKey: ['projects-metrics'],
    queryFn: async () =>
      apiRequest(
        '/task-module/projects/metrics',
        z.object({
          total_count: z.number(),
          at_risk_count: z.number(),
          overdue_count: z.number().optional(),
          portfolio_margin: z.object({
            amount: z.string(),
            currency: z.enum(['KZT', 'USD', 'EUR', 'RUB', 'CNY']),
          }),
          /**
           * D8.1 (DR-F4) — из скольких проектов сложен агрегат. Optional:
           * старый ответ без поля не должен ронять страницу, но и врать он
           * тогда не будет — без базы подпись просто не показывается.
           */
          portfolio_margin_basis: z
            .object({
              with_value: z.number(),
              total: z.number(),
              /** PLM-5 (B19) — проекты в другой валюте, не вошедшие в сумму. */
              excluded_other_currency_count: z.number().int().optional(),
            })
            .optional(),
          // PC-3: delta vs ~7d ago snapshot (null если snapshot ещё нет)
          delta_7d: z
            .object({
              total_count: z.number(),
              at_risk_count: z.number(),
              overdue_count: z.number(),
              portfolio_margin_amount: z.string(),
              baseline_captured_at: z.string(),
            })
            .nullable()
            .optional(),
          computed_at: z.string(),
        }),
      ),
    staleTime: 60_000,
  });
  // list-FE UX-3: BE-PROJ-3 /filter-counts (portfolio-wide). Fallback на rows.
  const countsQuery = useQuery({
    /**
     * Гейт фаз C/D (оба ревьюера) — счётчики считаются на ТОМ ЖЕ основании, что
     * выдача. С включённым тумблером сводка показывала «28 из 28», а чип «Все» —
     * 26: сводка считала с архивными, бакеты без них, и пользователь не знал,
     * каким числам верить.
     */
    queryKey: ['projects-filter-counts', includeArchived],
    queryFn: async () =>
      apiRequest(
        `/task-module/projects/filter-counts${includeArchived ? '?include_archived=true' : ''}`,
        z.object({
          all: z.number(),
          active: z.number(),
          at_risk: z.number(),
          overdue: z.number(),
          on_hold: z.number(),
          closed: z.number(),
          // F1 (project-actions-parity) — исход «Отменён» считается по `status`,
          // остальные бакеты — по стадии и здоровью.
          cancelled: z.number(),
          // F15 — знаменатель сводки «Показаны N из M» на основании ВЫДАЧИ.
          listed_total: z.number().optional(),
        }),
      ),
    staleTime: 30_000,
  });
  const counts = countsQuery.data ?? {
    all: rows.length,
    active: rows.filter((r) => r.stage === 'active').length,
    at_risk: rows.filter((r) => r.health === 'at_risk').length,
    overdue: rows.filter((r) => r.health === 'overdue').length,
    on_hold: rows.filter((r) => r.stage === 'on_hold').length,
    closed: rows.filter((r) => r.stage === 'closed').length,
    cancelled: rows.filter((r) => r.status === 'cancelled').length,
  };
  // F15 (гейт круга 1, обе половины): сводка брала знаменатель из чипа «Все»
  // (без отменённых) и рядом писала «включая 3 отменённых» — одно число для двух
  // выборок. Знаменатель — основание выдачи; чип «Все» остаётся своим числом.
  const listedTotal = countsQuery.data?.listed_total ?? counts.all + counts.cancelled;

  const columns: TableColumn<Project>[] = useMemo(
    () => [
      {
        id: 'code',
        header: m.project_col_code(),
        accessor: (r) => (
          <Link to="/projects/$id" params={{ id: r.id }} className={shared.accentLink}>
            {r.code}
          </Link>
        ),
        sticky: true,
        // 168px — колонка-идентификатор не должна резаться; измерено: «PROJ-2026-9001» 144px,
        // «PROJ-2026-SECRET» 166px (Inter 14px + padding). Вмещает оба.
        width: '168px',
        sortable: true,
      },
      // projects-by-company P2/P4 — «Компания» только в holding/all-охвате И не при группировке
      // по компании (тогда имя уже в band-заголовке секции — колонка дублирует, design-review).
      ...(showEntityColumn && !groupByCompany
        ? [
            {
              id: 'entity',
              header: m.project_col_entity(),
              // ellipsis + title: длинное имя компании не должно сжимать «Название»/«Клиент» (UI-review).
              accessor: (r: Project) => {
                const nm = entities[r.entity_id]?.name ?? r.entity_id.slice(0, 8);
                return (
                  <span
                    title={nm}
                    style={{
                      display: 'block',
                      maxWidth: '140px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {nm}
                  </span>
                );
              },
              width: '150px',
            } satisfies TableColumn<Project>,
          ]
        : []),
      {
        id: 'name',
        header: m.project_col_name(),
        accessor: (r) => r.name,
        // Модель эталона задач: у ВСЕХ колонок есть width, таблица 100%, колонки масштабируются
        // пропорционально (нет одного «поглотителя»). `name` — доминирующая колонка (широкий
        // default), но резайзится как все → резайз симметричен во ВСЕ стороны (раньше поглотитель
        // в середине давал асимметрию: слева ок, справа «съедал» влево — жалоба владельца).
        width: '560px',
        /**
         * Гейт фазы D — «Название» забирает свободную ширину.
         *
         * Комментарий выше описывает МОДЕЛЬ ДО F14: «у ВСЕХ колонок есть width,
         * таблица 100%, колонки масштабируются пропорционально». Ровно это
         * пропорциональное масштабирование и оказалось дефектом — на 2206px
         * множитель 1,4013, кебаб 44px рисовался 62px, перетаскивание на +80px
         * меняло ширину на +305px и сдвигало все соседние колонки.
         *
         * Симметрия перетаскивания, ради которой модель выбиралась, при этом не
         * теряется: «Название» — крайняя слева содержательная колонка, а остаток
         * теперь её, а не размазан по восьми. `560px` осталось минимумом на узких
         * окнах и стартовым значением после первого ручного перетаскивания.
         */
        grow: true,
        sortable: true,
      },
      {
        id: 'customer',
        // Пустая ячейка → «—» (как в таблице задач; было пусто — рваный скан, design-review).
        header: m.project_col_customer(),
        accessor: (r) => r.customer_name || r.customer_id || '—',
        width: '180px',
        // Второй проход гейта фазы D: с ростом одного лишь «Названия» имя клиента
        // обрезалось при свободном запасе рядом. Растут обе содержательные колонки.
        grow: true,
      },
      {
        /**
         * Дизайн-гейт фазы A `project-actions-parity` — колонка снова показывает
         * СТАДИЮ. Первая редакция подменяла её исходом «Отменён», и оба ревьюера
         * это отклонили одинаково: заголовок обещает стадию, колонка объявлена
         * `sortable` и сортирует по `stage`, а фильтр «Стадия = Планирование»
         * возвращал бы строку, подписанную «Отменён». Плюс терялся самый ценный
         * контекст отмены — на какой стадии проект убили.
         *
         * Подпись берётся из `PROJECT_STAGE_LABEL`, а не из карты `StatusBadge`:
         * та описывает business_status агента, и стадия `planning` подписывалась
         * ею как «Требует проверки» — руководитель читал проверку там, где
         * проект в планировании (вердикт UI, находка 1).
         */
        id: 'stage',
        header: m.project_col_stage(),
        accessor: (r) => {
          /**
           * Гейт фаз C/D (оба ревьюера, BLOCKER) — у архивной строки бейджи стадии
           * и здоровья гасятся. С включённым тумблером архивный проект шёл теми же
           * «В работе» + «В графике», что и живой: отличить его было нельзя, а
           * «В работе» на архивном прямо дезинформирует.
           */
          if (isArchivedRow(r)) {
            return (
              <StatusBadge status="terminated" label={m.project_row_archived_badge()} size="sm" />
            );
          }
          const b = stageBadge(r.stage);
          /**
           * PPC-F9 — решение владельца 06.08.2026: стадия ПОДЧИНЕНА исходу.
           *
           * «Отменённый проект не может идти „в работе“» — так это читалось, и
           * читалось верно: в трёх строках подряд стояли «В работе» и «Отменён».
           * Из трёх возможных лечений владелец выбрал третье: не гасить стадию
           * (это прячет факт) и не переводить её при отмене (это его стирает и
           * разворачивает решение прошлого пака), а показать подчинение —
           * «Отменён · В работе». Ортогональность стадии и исхода из `SOLUTION §9`
           * сохранена в ДАННЫХ; меняется только то, как строка читается.
           */
          if (r.status === 'cancelled') {
            const stageLabel = b.label || m.project_stage_unknown();
            /**
             * Раунд 2 гейта PPC (UI, MAJOR): «композитная плитка „Отменён · В работе“ —
             * два present-tense состояния в одном чипе одинаковым весом и тоном.
             * Строка читается как „проект отменён и одновременно в работе“».
             *
             * Верно, и это ровно та поломка, от которой уходило решение владельца.
             * Подчинение должно быть ВИДНО: исход — пилюля, стадия — тихий контекст
             * рядом с ней, а не второй равноправный сегмент внутри.
             */
            return (
              <span
                className={toolbar.stageWithOutcome}
                title={m.project_stage_cancelled_from_title({ stage: stageLabel })}
              >
                <StatusBadge status="terminated" label={PROJECT_STATUS_LABEL.cancelled} size="sm" />
                <span className={toolbar.stageSubordinate}>{stageLabel}</span>
              </span>
            );
          }
          /**
           * Пустая стадия называется пустой. Без этого `StatusBadge` подставлял свой
           * фолбэк «Без отклонений» — подпись словаря СОСТОЯНИЯ в колонке стадии
           * (находка NC-30 гейта пакета NC; здесь она на пути правки, и оставить её
           * значило бы выпустить «Отменён · Без отклонений» своими руками).
           */
          if (!b.label) {
            return <span className={toolbar.mutedCell}>{m.project_stage_unknown()}</span>;
          }
          /*
           * PPC-F22 (F13 tails-closeout): у отменённого подпись «на какой стадии» названа
           * тултипом, у завершённого — нет, и конвенция читалась только после обучения.
           * Тот же тултип у исхода «Завершён».
           */
          // Только когда стадия на момент завершения сама не финальная: «завершён на
          // стадии «Завершён»» — тавтология (живой кадр F13).
          if (r.status === 'completed' && r.stage !== 'completed' && r.stage !== 'closed') {
            return (
              <span title={m.project_stage_completed_at_title({ stage: b.label })}>
                <StatusBadge status={b.tone} label={b.label} size="sm" />
              </span>
            );
          }
          return <StatusBadge status={b.tone} label={b.label} size="sm" />;
        },
        // PPC-F9: в ячейке теперь пилюля исхода плюс тихая подпись стадии. Замер на
        // живом стенде: при 190px «Планирование» обрезалось многоточием — то есть
        // ровно та часть, ради которой решение владельца и принималось. 230px —
        // пилюля (90px) + зазор + самая длинная стадия («Планирование», 96px).
        width: '230px',
        sortable: true,
      },
      {
        /**
         * Исход вытесняет здоровье — но именно здесь, а не в «Стадии». Здоровье
         * отменённого проекта смысла не имеет, и до правки строка противоречила
         * сама себе: красный «Отменён» и зелёная галочка «Черновик готов» рядом
         * (вердикт UX, находка 2 — «успех подписывает отменённый проект»).
         */
        id: 'health',
        header: m.project_col_health(),
        accessor: (r) => {
          // У архивного здоровье смысла не имеет — как и у отменённого (фаза A).
          if (isArchivedRow(r)) return <span className={toolbar.mutedCell}>—</span>;
          /**
           * Раунд 1 гейта PPC, обе половины независимо: «„Отменён · Планирование“ в
           * „Стадии“ и „Отменён“ в „Состоянии“ — один факт, две колонки, одна и та
           * же иконка ⊘ дважды в строке» (UI); «исход назван дважды в соседних
           * ячейках, полезная часть (стадия) тонет» (UX).
           *
           * Решение прошлого гейта («исход вытесняет здоровье») принималось, когда
           * исхода не было НИГДЕ ещё: «В графике» на отменённом проекте — ложь, и
           * заменить её исходом было верно. Решение владельца 06.08 поселило исход
           * в колонке стадии, и прежняя замена стала повтором. Здоровье у
           * отменённого по-прежнему не показывается — но и не подменяется исходом:
           * колонка молчит ровно так же, как у архивной строки.
           */
          /**
           * PPC-F20 (раунд парности приёмки захода 5, обе половины): ветка гейтилась
           * ОДНИМ `cancelled`, и завершённый проект продолжал нести живой чип
           * «В графике» — «здоровье» у того, чей ход работ закончился. Условие
           * читает терминальный НАБОР исходов; сам набор — в `badge-tone`.
           */
          // PPC-F17: чип — только при отклонении; «В графике» — прочерк (как у терминальных).
          if (!healthChipVisible({ health: r.health, status: r.status })) {
            return <span className={toolbar.mutedCell}>—</span>;
          }
          const b = healthBadge({ health: r.health, status: r.status });
          return <StatusBadge status={b.tone} label={b.label} size="sm" />;
        },
        width: '150px',
        sortable: true,
      },
      {
        id: 'end_date',
        header: m.project_col_end_date(),
        // Консистентность с эталоном: тот же `fmtDate` задач («6 нояб. 2026 г.», текстовый
        // месяц, locale-aware), mono, LEFT-aligned — как колонка «Срок» в таблице задач
        // (было `formatDate` → «04.08.2026» + right-align, расходилось с эталоном).
        // F18 гейт (обе половины, PPC-F13): колонка моноширинная, и прочерк
        // набирался JetBrains Mono — короче и тоньше, чем «—» в «Клиенте».
        // Моно — только датам, пустое значение — шрифтом интерфейса.
        accessor: (r) =>
          fmtDate(r.expected_end_date ?? null) || <span className={styles.emptyValue}>—</span>,
        // 164px — измерено: самая широкая дата «30 сент. 2026 г.» = 135px текста (14px
        // JetBrains Mono) + 24px cell-padding + буфер. Узнее → обрезка «…202…» (design-review).
        width: '164px',
        mono: true,
        sortable: true,
      },
      {
        id: 'progress',
        header: m.project_col_progress(),
        /**
         * PPC-F20 — прогресс терминальной строки.
         *
         * Две разные поломки в одной колонке, и лечатся они по-разному.
         *
         * **Отменённый отдавал «0%»** тем же весом, что живые проценты. Это не ноль
         * работы, а отсутствие базы расчёта: считать долю выполненного у того, что
         * решили не делать, не от чего. Тот же класс, что `PPC-F11` («сметы нет»
         * вместо посчитанного нуля), только он не был доведён до этой колонки —
         * поэтому здесь прочерк с названной причиной, а не число.
         *
         * **Завершённый при <100% молча пропускался.** «Завершён» и «57%» в одной
         * строке — противоречие, и по формулировке находки его лечат ФЛАГОМ, а не
         * тишиной: число остаётся на месте (оно правда), рядом встаёт отметка,
         * которая называет противоречие вслух. Прятать процент здесь было бы хуже
         * дефекта: закрытый на середине проект — как раз то, что руководитель
         * обязан увидеть в реестре.
         */
        accessor: (r) => {
          if (r.status === 'cancelled') {
            return (
              <span className={toolbar.mutedCell} title={m.project_progress_cancelled_title()}>
                —
              </span>
            );
          }
          const pct = Number(r.percent_complete);
          const text = `${pct.toFixed(0)}%`;
          if (r.status === 'completed' && pct < 100) {
            /**
             * Раунд 1 гейта захода 6 (UX, MAJOR): «Флаг „⚠ 57%“ у завершённого
             * ALFA назван, но не объяснён: нет ни расшифровки, ни перехода к
             * причине. Противоречие показано и брошено».
             *
             * Подпись теперь ОБЪЯСНЯЕТ число, а не повторяет его: говорит, что
             * процент — это доля выполненных задач и что проект закрыт раньше
             * неё. Числа незакрытых задач здесь нет и не будет без денорма:
             * строка списка `task_summary` не несёт (его отдаёт только карточка
             * проекта), а тянуть сводку на каждую строку реестра ради подписи —
             * ровно тот N+1, от которого уходил пак производительности.
             *
             * Переход к разбору у строки уже есть — клик открывает карточку
             * проекта с задачами; ссылка внутри ячейки дала бы вторую цель в
             * одной строке и отобрала бы у первой предсказуемость.
             */
            const hint = m.project_progress_completed_below_full_title({ percent: text });
            return (
              <span className={toolbar.progressContradiction} title={hint} aria-label={hint}>
                <span aria-hidden="true">⚠</span>
                <span>{text}</span>
              </span>
            );
          }
          return text;
        },
        // 104px — вмещает заголовок «Прогресс» (90px обрезал до «Прогре…»).
        width: '104px',
        align: 'right',
        mono: true,
        sortable: true,
      },
      {
        /**
         * F5 (project-actions-parity §4.2) — видимый носитель меню в строке.
         *
         * До него меню строки открывалось ТОЛЬКО правым кликом: инвариант I1
         * («каждый пункт ПКМ достижим без ПКМ») держался формально — единственный
         * путь без ПКМ вёл через настройки проекта.
         *
         * **Ширина фиксированная и колонка НЕ поглотитель** (SOLUTION §8.2-О6):
         * ровно этот класс уже ломал нам таблицу задач — колонка без своей ширины
         * получала остаток, абсорбция гибла, и соседние ширины распирало при
         * drag-resize. Здесь 52px = 28px кнопки + 2 × 12px паддинга ячейки; `sortable`
         * не ставится — сортировать нечего.
         *
         * WTU-F22 (F16 пака `tails-closeout`): было 44px, содержимое (52) не влезало, и
         * `.tableFixed td { overflow: hidden; text-overflow: ellipsis }` рисовал в
         * оставшихся 4px многоточие — видна была одна его точка, «засечка справа от
         * кебаба в каждой строке». Замер: td 1359–1403, кнопка 1371–1399, точка x≈1401.
         */
        id: 'actions',
        header: '',
        accessor: (r) => (
          <button
            type="button"
            className={toolbar.rowMenuBtn}
            aria-label={m.ctx_project_menu_aria()}
            aria-haspopup="menu"
            onClick={(e) => {
              // Клик по кебабу не должен открывать проект: строка кликабельна целиком.
              e.stopPropagation();
              openContextMenuFromButton(e.currentTarget, buildProjectMenu(toProjectMenuTarget(r)));
            }}
          >
            <MoreHorizontal size={16} aria-hidden="true" />
          </button>
        ),
        width: '52px',
        align: 'center',
      },
    ],
    [showEntityColumn, entities, groupByCompany, buildProjectMenu],
  );

  // F3 — whitelist сортируемых колонок (совпадает с BE-enum B1; §2.3).
  const SORTABLE_COLUMNS = useMemo(
    () => new Set(['code', 'name', 'stage', 'health', 'end_date', 'progress']),
    [],
  );
  // F3 — дескрипторы для «Колонки»-пикера. Набор зеркалит РЕНДЕРимые колонки (entity —
  // только в holding/all и не при group-by), 'code' — pinned-left. Persist-order/hidden
  // толерантны к отсутствию entity вне scope (rank-map игнорит незнакомый id, §5).
  const columnLabels: Record<string, string> = {
    code: m.project_col_code(),
    entity: m.project_col_entity(),
    name: m.project_col_name(),
    customer: m.project_col_customer(),
    stage: m.project_col_stage(),
    health: m.project_col_health(),
    end_date: m.project_col_end_date(),
    progress: m.project_col_progress(),
  };
  const columnDescriptors: ColumnDescriptor[] = columns.map((c) => ({
    id: c.id,
    label: columnLabels[c.id] ?? c.id,
    ...(c.id === 'code' ? { pinned: true } : {}),
  }));
  // F3 — видимые id для «Колонки»-пикера (чекбоксы). 'code' всегда видима.
  const visibleColumnIds = useMemo(
    () =>
      new Set(
        columns.filter((c) => c.id === 'code' || !tableHidden.includes(c.id)).map((c) => c.id),
      ),
    [columns, tableHidden],
  );
  // F3 — видимые колонки: фильтр по hidden ('code' всегда) + порядок по rank-map
  // ('code' первой, далее по tableOrder, неперечисленные — в конец; SOLUTION §5).
  const visibleColumns = useMemo(() => {
    const shown = columns.filter((c) => c.id === 'code' || !tableHidden.includes(c.id));
    if (tableOrder.length === 0) return shown;
    const rank = new Map(tableOrder.map((id, i) => [id, i]));
    return [...shown].sort((a, b) => {
      if (a.id === 'code') return -1;
      if (b.id === 'code') return 1;
      return (
        (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER)
      );
    });
  }, [columns, tableHidden, tableOrder]);
  // F3 — мапа ширин (стор → Map) для resizableColumns (все колонки, включая name).
  const columnWidthsMap = useMemo(() => new Map(Object.entries(tableWidths)), [tableWidths]);
  // F3 — controlled sort: Table вызывает только для sortable-колонок; гвард на whitelist.
  const onTableSortChange = (next: ProjectsTableSort): void => {
    if (next && !SORTABLE_COLUMNS.has(next.id)) return;
    setTableSort(next);
  };
  const entityOptions = Object.entries(entities).map(([id, v]) => ({ value: id, label: v.name }));
  // F5 — rule-builder состояние из URL (шэрабл): stage/health/visibility/entity/end_date.
  // Реконструируем правила для карточки-конструктора и чипов; запись — обратно в URL.
  const projectRules = useMemo(
    () =>
      queryToProjectRules({
        stage: stage || undefined,
        stage_not: search.stage_not,
        health: health || undefined,
        health_not: search.health_not,
        visibility: search.visibility,
        entity_id: entityId || undefined,
        expected_end_from: search.expected_end_from,
        expected_end_to: search.expected_end_to,
        end_date_empty: search.end_date_empty,
      }),
    [
      stage,
      health,
      entityId,
      search.stage_not,
      search.health_not,
      search.visibility,
      search.expected_end_from,
      search.expected_end_to,
      search.end_date_empty,
    ],
  );
  // F5 — число активных правил для бейджа «Фильтр N» (эталонный FilterBar).
  const activeFilterCount = projectRules.rules.length;
  // F6 — колоночные воронки через `ColumnFilterPopover` (эталон, spec-driven — не чужой
  // NotifColumnFilter). Проектные field-spec; мультивыбор (in_list) → CSV в URL → BE inArray
  // (B1). Единый источник — URL (тот же, что бакеты/попап).
  const projectFilterFields = buildProjectFilterFields(entityOptions, showEntityColumn);
  const funnelCfg: Record<
    string,
    { field: string; get: () => string; set: (csv?: string) => void }
  > = {
    stage: { field: 'stage', get: () => stage, set: (csv) => setSearch({ stage: csv }) },
    health: { field: 'health', get: () => health, set: (csv) => setSearch({ health: csv }) },
    entity: { field: 'entity', get: () => entityId, set: (csv) => setSearch({ entity_id: csv }) },
  };
  const renderProjectColumnFilter = (col: TableColumn<Project>): React.ReactNode => {
    const cfg = funnelCfg[col.id];
    if (!cfg) return null;
    if (col.id === 'entity' && !showEntityColumn) return null;
    const spec = projectFilterFields.find((s) => s.field === cfg.field);
    if (!spec) return null;
    const cur = cfg.get();
    const rule: FilterRule | undefined = cur
      ? { field: cfg.field, operator: 'in_list', value: cur.split(',') }
      : undefined;
    return (
      <ColumnFilterPopover
        spec={spec}
        rule={rule}
        onChange={(r) => {
          const arr = r && Array.isArray(r.value) ? (r.value as string[]) : [];
          cfg.set(arr.length ? arr.join(',') : undefined);
        }}
      />
    );
  };

  // projects-by-company P4 — единый рендер списка (cards | table) для flat и секций группировки.
  // PPC-F21 (F13 tails-closeout): терминальные и архивные — вниз, стабильно (terminal-last.ts).
  const renderList = (rawList: Project[]): JSX.Element => {
    const list = terminalLast(rawList);
    return effectiveView === 'cards' ? (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
          gap: 20,
        }}
      >
        {list.map((p) => (
          <ProjectCard
            key={p.id}
            project={p}
            {...(showEntityColumn ? { entityName: entityName(p.entity_id) } : {})}
          />
        ))}
      </div>
    ) : (
      // F-grid — обёртка `.tableWrap` даёт те же вертикальные hairline-разделители колонок
      // и усиленную границу шапки, что у эталонной таблицы задач (GlobalTableLayout .wrap).
      <div className={toolbar.tableWrap}>
        <Table
          data={list}
          columns={visibleColumns}
          loading={query.isLoading}
          rowKey={(r) => r.id}
          /**
           * Раунд 1 гейта PPC (UI, MAJOR): «строки отменённых в реестре не
           * приглушены — название и код той же яркости, что у активных. В-10
           * применён к карточке, к таблице нет». Правило В-10 — про терминальность
           * как состояние, а не про одну поверхность: там, где решение читается,
           * оно должно читаться одинаково.
           */
          rowClassName={(r) =>
            r.status === 'cancelled' || isArchivedRow(r) ? 'rowMuted' : undefined
          }
          ariaLabel={m.project_title_list()}
          layout="fixed"
          stickyHeader
          stickyFirstColumn
          gridNav
          density="compact"
          resizableColumns
          columnWidths={columnWidthsMap}
          onColumnResize={setTableWidth}
          controlledSort={tableSort}
          onSortChange={onTableSortChange}
          renderColumnFilter={renderProjectColumnFilter}
          // F14 — ПКМ по строке проекта: тот же примитив и тот же реестр (I2).
          onRowContextMenu={(row, e) => {
            openContextMenuFromEvent(e, buildProjectMenu(toProjectMenuTarget(row)));
          }}
        />
      </div>
    );
  };

  return (
    // F1 — шелл-паритет: WorkspaceSurface (full-bleed header-слот + единый скролл-регион)
    // вместо PageHeader-в-`<div>`. `/projects` зарегистрирован в `_auth.tsx` isWorkspace →
    // `.main.workspace` снимает max-width/padding и даёт flex-высоту (§7-L9).
    <WorkspaceSurface
      header={
        <>
          <h1 className={toolbar.title}>{m.project_title_list()}</h1>
          {/* projects-by-company F-1 — scope-CTA у заголовка (паритет с эталоном). */}
          <ScopeAllCompaniesCta />
          {/* F2 — сегмент-view-switcher icon+label, паритет с эталоном (.viewSwitcher).
              Mobile force-cards: свитчер скрыт (FE-88). */}
          <div
            className={toolbar.viewSwitcher}
            style={{ display: isMobile ? 'none' : 'inline-flex' }}
            role="radiogroup"
            aria-label={m.project_view_mode_aria()}
          >
            <button
              type="button"
              role="radio"
              aria-checked={view === 'cards'}
              onClick={() => setView('cards')}
              className={[toolbar.viewBtn, view === 'cards' ? toolbar.viewActive : '']
                .filter(Boolean)
                .join(' ')}
            >
              <Grid3x3 size={14} aria-hidden="true" /> {m.project_view_cards_aria()}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={view === 'table'}
              onClick={() => setView('table')}
              className={[toolbar.viewBtn, view === 'table' ? toolbar.viewActive : '']
                .filter(Boolean)
                .join(' ')}
            >
              <ListIcon size={14} aria-hidden="true" /> {m.project_view_table_aria()}
            </button>
          </div>
          {/* F1 — спейсер отжимает вторичные контролы вправо (паритет эталонного header-ряда). */}
          <div className={toolbar.headerSpacer} />
          {/* F3 (D9) — «Показывать завершённые» toggle (паритет с эталоном .viewBtn Eye/EyeOff). */}
          {!isMobile && (
            <button
              type="button"
              onClick={() => setShowClosed(!showClosed)}
              aria-pressed={showClosed}
              title={m.tm_show_closed()}
              className={[toolbar.viewBtn, showClosed ? toolbar.viewActive : '']
                .filter(Boolean)
                .join(' ')}
            >
              {showClosed ? (
                <Eye size={14} aria-hidden="true" />
              ) : (
                <EyeOff size={14} aria-hidden="true" />
              )}
              {m.tm_show_closed()}
            </button>
          )}
          {/**
           * F10 (project-actions-parity §8.2-О3) — «Показывать архивные».
           * Стоит рядом с «Показывать завершённые»: обе кнопки про одно — расширить
           * выдачу тем, что по умолчанию убрано с глаз. Разница названа словами:
           * завершённые — исход, архивные — видимость.
           */}
          {!isMobile && (
            <button
              type="button"
              className={[toolbar.filterBtn, includeArchived ? toolbar.filterActive : '']
                .filter(Boolean)
                .join(' ')}
              aria-pressed={includeArchived}
              onClick={() => setIncludeArchived(!includeArchived)}
            >
              {includeArchived ? <Eye size={14} /> : <EyeOff size={14} />}
              {m.project_show_archived()}
            </button>
          )}
          {/* F7 — page-size сразу за «Показывать завершённые» (порядок кластера эталона:
              showClosed → 100▾ → Вид → Колонки). */}
          {!isMobile && effectiveView === 'table' && (
            <Select
              size="sm"
              hideLabel
              label={m.project_page_size_label()}
              value={String(pageSize)}
              options={PROJECTS_PAGE_SIZE_OPTIONS.map((n) => ({
                value: String(n),
                label: String(n),
              }))}
              onChange={(e) => setPageSize(Number(e.target.value))}
            />
          )}
          {/* F4 — «Вид» (группировка+сортировка) попап, паритет с эталоном DisplayPanel.
                Группировка по компании работает в ОБОИХ видах (§7-L5); сорт влияет и на
                порядок карточек. Header-click сорт таблицы сохранён (data-table, §7-L4). */}
          {!isMobile && (
            <ProjectDisplayPanel
              groupByCompany={groupByCompany}
              onToggleGroupByCompany={toggleGroupByCompany}
              showGroupByCompany={showEntityColumn}
              sort={tableSort}
              onSortChange={onTableSortChange}
            />
          )}
          {/* F3 — «Колонки»: выбор/порядок/сброс. Только табличный вид (desktop). */}
          {!isMobile && effectiveView === 'table' && (
            <ColumnConfigPopover
              columns={columnDescriptors}
              visible={visibleColumnIds}
              onToggle={toggleTableHidden}
              onReorder={setTableOrder}
              onReset={resetTableColumns}
              scopeLabel={m.project_title_list()}
            />
          )}
          {canCreate && (
            // list-FE UI-6/UI-7/UI-8: size=sm для consistency с view-toggle buttons
            // (раньше default Button height 36-40px не сходился с view-toggle 32px,
            // CTA wraps 2 lines при narrow viewport).
            <Button
              size="sm"
              onClick={() => navigate({ to: '/projects/new' })}
              style={{ whiteSpace: 'nowrap' }}
            >
              <Plus size={14} /> {m.project_cta_create()}
            </Button>
          )}
        </>
      }
    >
      {/* F1 — контент surface: flex-колонка (gap) с padding, левый край выровнен с заголовком. */}
      <div className={toolbar.surfaceMain}>
        {/* D3.6a (Z-F13) — построчный разбор роняет непарсящуюся запись, и до этой
          строки делал это молча: пользователь видел на карточку меньше без единого
          намёка, что она существует. Норма — ноль, строка появляется только при
          реальной потере.
          Место и вид — по вердикту дизайн-гейта D3, раунд 2. Внутри ряда метрик она
          стояла бок о бок с «Маржа: …», различаясь только цветом, и читалась как
          продолжение метрики: сообщение об утрате данных склеивалось с деньгами.
          Теперь — отдельная строка над рядом, полной шириной, со значком (цвет не
          может быть единственным носителем статуса, §1.3) и `role="status"`.
          Текста «мы уже разбираемся» здесь нет намеренно: обещание от первого лица
          непроверяемо. Строка называет факт; след для разбора уходит телеметрией.
          Собственного знаменателя строка не несёт — он на поверхности один, у
          счётчика «Показаны N из M». */}
        {droppedRows > 0 && (
          <div
            role="status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--fs-12)',
              color: 'var(--warn-text)',
            }}
          >
            <AlertTriangle size={14} aria-hidden="true" />
            <span>{m.project_page_dropped_rows({ count: droppedRows })}</span>
            {/* D8.2 (DR-F7, DR-F10) — след для разбора. §6.3 требует от ошибки
                повтор и след: повтор был, следа не было — телеметрия уходила
                fire-and-forget, и руководитель не мог назвать поддержке, ЧТО
                именно не открылось. Идентификатор рождается на клиенте и с ним
                же уезжает в телеметрию, поэтому строка на экране и запись в
                серверном логе сходятся по одному числу. */}
            {droppedTraceId !== null && (
              <span
                style={{
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-11)',
                  userSelect: 'all',
                }}
              >
                {m.error_trace_label()} {droppedTraceId}
              </span>
            )}
          </div>
        )}
        {/* Density-rework (жалоба владельца + UX-review «Hero dominant»): 3-карточный Hero
          убран — ВСЕ/ПОД РИСКОМ дословно дублировали бакеты, МАРЖА была единственной
          портфельной. Осталась одна тонкая портфельная строка (маржа) в ряду управления
          ниже. Таблица поднимается к плотности эталона /work-tasks (SOLUTION §6.2-L14). */}
        {/* F7 — ряд управления НАД бакетами (порядок эталона: [фильтр/поиск-ряд] → [чипы]).
          Метрик-зона слева, поиск+«Фильтр» отжаты вправо (эталон right-align: search → Фильтр). */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Density-rework + UI-review polish: метрик-зона (счётчик «Показаны N из M» +
            портфельная «Маржа») — теперь СЛЕВА (эталон держит слева tabs, справа — поиск/Фильтр). */}
          {(metricsQuery.data ||
            (effectiveView === 'table' && !query.isLoading && rows.length > 0)) && (
            <div className={styles.metrics}>
              {/* D8.2 (DR-F6) — счётчик рендерится в ОБОИХ видах. В «Карточках»
                  его не было вовсе, и при показанном уведомлении о потере
                  пользователь не имел под рукой ни знаменателя, ни повтора —
                  ровно то дополнение, которое ревьюер дописал в раунде 3. */}
              {/* F15 круг 2 (UX, MAJOR): при пустой выдаче сводка пропадала, и
                  «0 из 34 · 34 скрыты запросом» — ровно то, что объясняет пустоту, —
                  не звучало. Сводка живёт, пока в портфеле есть что показывать. */}
              {!query.isLoading && (rows.length > 0 || listedTotal > 0) && (
                <span className={styles.metricsGroup}>
                  {/* Раунд 2 гейта — разница со счётчиком чипов названа СЛОВАМИ
                      и в потоке, а не спрятана в нативный `title`: подсказка на
                      обёртке не имеет ни видимого маркера, ни фокуса, ни тача,
                      то есть объяснение существует только для мыши.

                      Раунд 3 — знаменателем стал ПОРТФЕЛЬНЫЙ итог, а причина
                      названа точно, когда она одна. «22 из 22» не несло сигнала:
                      знаменатель считался уже после фильтра, и сходимость с
                      чипом «24» пользователь достраивал в уме. А слово
                      «фильтрами» указывало на НЕ ТОТ орган управления —
                      пользователь шёл в свёрнутую панель «Фильтр», а рычаг лежит
                      в шапке («Показывать завершённые»). Правило: ровно одна
                      активная причина — называем её; две и больше — обобщаем,
                      и тогда панель фильтров действительно нужное место. */}
                  {listedTotal > totalCount
                    ? m.project_page_shown({ shown: rows.length, total: listedTotal })
                    : m.project_page_shown({ shown: rows.length, total: totalCount })}
                  {/* Раунд 3 гейта NC (UX, 🔴 BLOCKER): «активный чип „30 Все“ и подпись
                      „Показаны 33 из 33“ стоят в одном поле зрения и оба отвечают на
                      вопрос „сколько у меня проектов“, разными числами, без единого
                      слова о том, откуда разница». Разница ровно в отменённых: чипы
                      левой оси их не считают (у них своя ось за швом), выдача —
                      считает. Называем состав выдачи словами; МОДЕЛЬ счёта не
                      трогаем — она общая с таблицей и заведена гейтом D8.2, и её
                      смена признана обеими половинами отдельной задачей. */}
                  {!status && counts.cancelled > 0 && (
                    <span className={styles.metricsAside}>
                      {' · '}
                      {m.project_page_shown_incl_cancelled({ count: counts.cancelled })}
                    </span>
                  )}
                  {listedTotal > totalCount && (
                    <span className={styles.metricsAside}>
                      {' · '}
                      {/**
                       * Раунд 3 гейта захода 6 (UX, MAJOR): «Сужение списка
                       * приписано не тому органу управления — „24 скрыты
                       * фильтрами“ при пустой кнопке „Фильтр“ и активном
                       * поисковом запросе „проект“ ведёт пользователя чистить
                       * фильтры, где чистить нечего».
                       *
                       * Причина называется буквально: поиск — поиском, фильтры —
                       * фильтрами. И там, где причина одна и она поисковая,
                       * рядом стоит действие, которое её снимает: чистить надо
                       * тот орган, который сузил.
                       */}
                      {q && !hasNonQueryFilters && !hideClosed ? (
                        <>
                          {m.project_page_hidden_by_query({
                            count: listedTotal - totalCount,
                            query: q,
                          })}
                          {/* F15 круг 3 (UI, MINOR): при пустой выдаче снятие поиска
                              стоит в пустом состоянии — четвёртый путь к нему лишний. */}
                          {rows.length > 0 && (
                            <>
                              {' '}
                              <button
                                type="button"
                                className={styles.inlineClear}
                                onClick={() => setQ('')}
                              >
                                {m.project_page_clear_query()}
                              </button>
                            </>
                          )}
                        </>
                      ) : hideClosed && !hasOtherFilters ? (
                        m.project_page_hidden_completed({ count: listedTotal - totalCount })
                      ) : (
                        m.project_page_hidden_by_filters({ count: listedTotal - totalCount })
                      )}
                    </span>
                  )}
                </span>
              )}
              {/* D8.1 (DR-F4) — агрегат перестал выдавать «0,00 ₸» за факт.
                  `COALESCE` на сервере превращал отсутствие данных в валидную
                  сумму, и портфель без единой посчитанной маржи выглядел так же,
                  как портфель, где она посчитана и равна нулю. Теперь: ни одного
                  слагаемого — «нет данных»; часть — сумма с честной базой
                  «по N из M»; все — сумма без оговорки. */}
              {metricsQuery.data &&
                (() => {
                  const basis = metricsQuery.data.portfolio_margin_basis;
                  /**
                   * PLM-5 (F17 пака tails-closeout, BE — B19): маржа портфеля
                   * суммирует только KZT, и проекты в других валютах выпадали
                   * молча. До FX-нормализации исключение называется числом.
                   */
                  const excludedFx = basis?.excluded_other_currency_count ?? 0;
                  const fxNote =
                    excludedFx > 0 ? (
                      <span className={styles.metricsAside}>
                        {' · '}
                        {m.project_margin_excluded_currency({ count: excludedFx })}
                      </span>
                    ) : null;
                  if (basis && basis.with_value === 0) {
                    return (
                      <span className={`${styles.metricsGroup} ${styles.metricsAside}`}>
                        {m.project_financial_gross_margin()}: {m.common_empty_state_no_data()}
                        {fxNote}
                      </span>
                    );
                  }
                  return (
                    <span className={styles.metricsGroup}>
                      {m.project_financial_gross_margin()}:{' '}
                      {formatMoney(metricsQuery.data.portfolio_margin)}
                      {basis && basis.with_value < basis.total && (
                        <span className={styles.metricsAside}>
                          {' · '}
                          {m.project_margin_basis({ withValue: basis.with_value })}
                        </span>
                      )}
                      {fxNote}
                    </span>
                  );
                })()}
            </div>
          )}
          {/* Спейсер отжимает поиск+«Фильтр» вправо (right-align кластера, эталон). */}
          <div style={{ flex: '1 1 auto' }} />
          {/* FE-43: leadingIcon Search + clear X. Поиск (code+name). Порядок эталона: поиск, затем Фильтр. */}
          <Input
            label={m.project_filter_code_placeholder()}
            hideLabel
            placeholder={m.project_filter_code_placeholder()}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            leadingIcon={<Search size={14} />}
            trailingIcon={
              q ? (
                <button
                  type="button"
                  onClick={() => setQ('')}
                  aria-label={m.common_button_cancel()}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    color: 'inherit',
                    display: 'inline-flex',
                  }}
                >
                  <X size={14} />
                </button>
              ) : undefined
            }
          />
          {/* F5 — «Фильтр N»: эталонный rule-builder (FilterCard в TaskPopover). Справа от поиска. */}
          <TaskPopover
            width={480}
            align="end"
            trigger={(open, isOpen) => (
              <button
                type="button"
                className={[
                  toolbar.filterBtn,
                  activeFilterCount > 0 ? toolbar.filterActive : '',
                  isOpen ? toolbar.filterOpen : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={open}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
              >
                <Filter size={14} aria-hidden /> {m.tm_filter()}
                {activeFilterCount > 0 && (
                  <span className={toolbar.badge}>{activeFilterCount}</span>
                )}
              </button>
            )}
          >
            {/* resultCount не передаём: у эталона превью «→ N задач» (task-worded), а
              актуальный счётчик проектов уже виден в метрик-зоне «Показаны N из M». */}
            {(close) => (
              <FilterCard
                value={projectRules}
                fields={projectFilterFields}
                onChange={(next) => setSearch(projectRulesToQuery(next))}
                onApply={close}
              />
            )}
          </TaskPopover>
        </div>
        {/* F3 — быстрые фильтры как quick-chip пилюли (паритет с эталоном .heroRow: счётчик-
          префикс + active-стиль). Семантика бакетов эксклюзивная (stage/health single, §7-L3) —
          визуально чип тот же. */}
        {/* D8.2 (DR-F6) — чипы объявляют СВОЮ базу. На поверхности жили три
            числа: счётчик отвечал на текущий фильтр, чипы считали портфель по
            серверу ДО фильтра, и ни одно не объясняло другое. Для учётного
            продукта это дороже самой потери записи. Сводить их к одному
            источнику нельзя — они отвечают на разные вопросы («сколько показано»
            против «сколько всего в каждом бакете»); поэтому база названа
            словами там, где её читают. */}
        {/* NC-16 (свод tails-sweep-2, заход 5): база чипов названа ВИДИМО, а не
            только в нативном `title`. Подсказка на обёртке не имеет ни маркера,
            ни фокуса, ни тача — то есть объяснение расхождения «чип 24 против 22
            карточек» существовало только для мыши, и ровно оно снимает вопрос
            «кто из двух чисел врёт». Считать чипы по показанному нельзя: они
            отвечают на другой вопрос (сколько всего в каждом бакете), и тогда
            бакет с нулём исчезал бы вместе с возможностью в него перейти. */}
        {/* F15 (пак tails-closeout, решение В-13): «подпись описывает ряд, легенда
            над рядом». Легенда стояла ПОД чипами, и пользователь сначала видел
            несовпадение «30 против шести строк», а объяснение — потом (PPC-F24).
            Спор двух гейтов о месте подписи (ось слева или ряд целиком, PPC-F16)
            снят решением: подпись говорит о ВСЁМ ряде и сама называет исключение
            «Отменённых». При активном поиске или фильтре легенда говорит, что они
            счётчики не сужают, а сами счётчики помечаются как портфельные —
            визуально, не только словами (PPC-F23: «6 Активные» при шести строках
            читалось связью). */}
        <p className={styles.bucketsBasis} id="projects-buckets-legend">
          {/* Гейт круга 1 (обе половины, MAJOR): тумблер архивных двигает счётчики
              (31→32), а легенда говорила «по всему портфелю». Легенда называет,
              входят ли архивные, — и тогда «фильтры не сужают» остаётся правдой. */}
          {(narrowed ? m.project_buckets_legend_narrowed : m.project_buckets_legend)({
            scope: includeArchived
              ? m.project_buckets_scope_with_archived()
              : m.project_buckets_scope_live(),
          })}
        </p>
        <div
          className={styles.bucketsRow}
          title={m.project_buckets_basis_hint()}
          aria-describedby="projects-buckets-legend"
        >
          {/* Раунд 2 гейта NC (UI, MINOR): подпись стояла слева от ВСЕЙ строки и
              по расположению накрывала в том числе «Отменённые» за швом — а для
              них исключение как раз не действует. Подпись переехала внутрь левой
              группы, к тем чипам, о которых говорит. */}
          {(
            [
              {
                key: 'all',
                label: m.project_bucket_all(),
                count: counts.all,
                active: !stage && !health && !status,
                disabled: false,
                onClick: () => {
                  setStage('');
                  setHealth('');
                  setStatus('');
                },
              },
              {
                key: 'active',
                label: m.project_bucket_active(),
                count: counts.active,
                active: stage === 'active' && !health && !status,
                disabled: counts.active === 0 && stage !== 'active',
                onClick: () => {
                  setStage('active');
                  setHealth('');
                  setStatus('');
                },
              },
              {
                key: 'at_risk',
                label: m.project_bucket_at_risk(),
                count: counts.at_risk,
                active: health === 'at_risk' && !status,
                disabled: counts.at_risk === 0 && health !== 'at_risk',
                onClick: () => {
                  setStage('');
                  setHealth('at_risk');
                  setStatus('');
                },
              },
              {
                key: 'overdue',
                label: m.project_bucket_overdue(),
                count: counts.overdue,
                active: health === 'overdue' && !status,
                disabled: counts.overdue === 0 && health !== 'overdue',
                onClick: () => {
                  setStage('');
                  setHealth('overdue');
                  setStatus('');
                },
              },
              {
                key: 'on_hold',
                label: m.project_bucket_on_hold(),
                count: counts.on_hold,
                active: stage === 'on_hold' && !status,
                disabled: counts.on_hold === 0 && stage !== 'on_hold',
                onClick: () => {
                  setStage('on_hold');
                  setHealth('');
                  setStatus('');
                },
              },
              {
                key: 'closed',
                label: m.project_bucket_closed(),
                count: counts.closed,
                active: stage === 'closed' && !status,
                disabled: counts.closed === 0 && stage !== 'closed',
                onClick: () => {
                  setStage('closed');
                  setHealth('');
                  setStatus('');
                },
              },
              {
                /**
                 * F1 (project-actions-parity §1.1) — единственный бакет, который
                 * отбирает по исходу, а не по стадии. Отмена в `stage` не выражается
                 * и не будет: отменённый проект остаётся на своей стадии, а исход
                 * пишется в `status`. Отсюда и `setStage('')` — иначе выбор стадии
                 * и выбор исхода схлопывались бы в пустую выдачу.
                 */
                key: 'cancelled',
                label: m.project_bucket_cancelled(),
                count: counts.cancelled,
                active: status === 'cancelled',
                disabled: counts.cancelled === 0 && status !== 'cancelled',
                // Дизайн-гейт фазы A — визуальный развод осей: шесть чипов слева
                // отбирают по стадии и здоровью, этот — по исходу. Без разделителя
                // он читался полным близнецом соседей, и пересечение множеств
                // (стадия отменённого осталась `planning`) становилось невидимым.
                startsNewAxis: true,
                onClick: () => {
                  setStage('');
                  setHealth('');
                  setStatus('cancelled');
                },
              },
            ] as const
          ).map((b) => (
            <Fragment key={b.key}>
              {'startsNewAxis' in b && b.startsNewAxis && (
                <span className={toolbar.quickChipAxisRule} aria-hidden="true" />
              )}
              <button
                type="button"
                onClick={b.onClick}
                disabled={b.disabled}
                aria-pressed={b.active}
                className={[toolbar.quickChip, b.active ? toolbar.quickChipActive : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <span
                  className={[toolbar.quickChipCount, narrowed ? styles.countPortfolio : '']
                    .filter(Boolean)
                    .join(' ')}
                  // Пунктир обещает пояснение по наведению (гейт круга 1, MINOR) —
                  // оно здесь, на самом числе.
                  {...(narrowed ? { title: m.project_buckets_basis_hint() } : {})}
                >
                  {b.count}
                </span>{' '}
                {b.label}
              </button>
            </Fragment>
          ))}
        </div>
        {/**
         * База счётчиков — СТРОКОЙ НИЖЕ, а не внутри ряда чипов.
         *
         * Раунд 2 гейта NC (UI, MINOR) поставил её между последним чипом левой
         * оси и швом, чтобы она не накрывала «Отменённые», для которых
         * исключение не действует. Раунд 2 гейта захода 6 (UX): «подпись … не
         * принадлежит визуально ни одной из них — читателю приходится
         * угадывать, к каким числам она относится»; раунд 3 (UI): «пояснение …
         * стоит прозой внутри строки чипов и отрывает чип „Отменённые“ от
         * группы. Легенду — строкой ниже, `--text-xs` muted».
         *
         * Обе половины сошлись на одном: подпись — легенда ряда, а не его
         * элемент. Место сменилось, а причина прошлого решения сохранена
         * текстом: подпись сама называет, О КАКИХ счётчиках речь.
         */}
        {/* F5 — inline-панель селектов удалена: её роль (Видимость / Компания) перешла в
          rule-builder «Фильтр N» (FilterCard, поля visibility/entity), а колоночные воронки
          (F6) фильтруют stage/health/entity из шапки таблицы. Фильтр по customer_id (сырой
          UUID-input, аналога у эталона-задач нет) снят — SOLUTION §7-L14. */}
        {/* F5 — счётчик «Показаны N из M» перенесён в правую метрик-зону ряда управления
          (UI-review polish: инлайн, минус отдельная горизонтальная полоса). */}
        {rows.length === 0 && !query.isLoading ? (
          /* FE-90 (Sprint 11) — filter-empty с FilterX icon + meaningful title + actionable CTAs.
           FE-136 (Sprint 12 polish from S11-FE90-5): visual separation —
           margin-top для отделения empty-block от filter-bar выше. */
          stage ||
          health ||
          status ||
          q ||
          customerId ||
          entityId ||
          search.visibility ||
          search.stage_not ||
          search.health_not ||
          search.expected_end_from ||
          search.expected_end_to ||
          search.end_date_empty ? (
            <div
              style={{
                padding: '32px 24px',
                textAlign: 'center',
                border: '1px dashed var(--border)',
                borderRadius: 8,
                color: 'var(--text-muted)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                marginTop: 24,
              }}
            >
              {/* FE-132 (Sprint 12 polish from S11-FE90-1): icon 48px + accent-tint
                round background для visual anchor, не lone-icon на фоне. */}
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: 'rgba(217, 119, 6, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-warning, #d97706)',
                }}
              >
                <FilterX size={32} aria-hidden="true" />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    marginBottom: 4,
                  }}
                >
                  {searchOnly
                    ? m.project_search_empty_title({ q })
                    : m.project_filter_empty_title()}
                </div>
                <div style={{ fontSize: 13, opacity: 0.7, maxWidth: 400 }}>
                  {searchOnly ? m.project_search_empty_hint() : m.project_filter_empty_hint()}
                </div>
              </div>
              {/* FE-135 (Sprint 12 polish from S11-FE90-4): context-aware
                inline chips для granular снятия фильтров, не all-or-nothing. */}
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  fontSize: 12,
                }}
              >
                {stage && (
                  <FilterChip
                    label={`${m.project_col_stage()}: ${
                      PROJECT_STAGE_LABEL[stage as ProjectStage] ?? stage
                    }`}
                    onClear={() => setStage('')}
                  />
                )}
                {health && (
                  <FilterChip
                    label={`${m.project_col_health()}: ${
                      PROJECT_HEALTH_LABEL[health as ProjectHealth] ?? health
                    }`}
                    onClear={() => setHealth('')}
                  />
                )}
                {q && (
                  <FilterChip
                    label={m.project_filter_search_chip({ q })}
                    onClear={() => setQ('')}
                  />
                )}
                {customerId && (
                  <FilterChip
                    label={`${m.project_col_customer()}: ${customerId.slice(0, 8)}…`}
                    onClear={() => setCustomerId('')}
                  />
                )}
                {/* P4 — чип активного фильтра по компании. */}
                {entityId && (
                  <FilterChip
                    label={`${m.project_col_entity()}: ${entityName(entityId)}`}
                    onClear={() => setEntityId('')}
                  />
                )}
                {/* F4 — чип видимости (подметён скептиком #15). */}
                {search.visibility && (
                  <FilterChip
                    label={`${m.project_visibility_label()}: ${
                      search.visibility === 'open'
                        ? m.project_visibility_open()
                        : m.project_visibility_private()
                    }`}
                    onClear={() => setSearch({ visibility: undefined })}
                  />
                )}
              </div>
              {/* FE-133 (Sprint 12 polish from S11-FE90-2): primary = recovery
                action (Сбросить фильтры), secondary = create. Не competing-equal. */}
              {/* F15 круг 3 (UI, MAJOR): §4.1 — одно основное действие на экран, и
                  оно уже в шапке («Создать проект»). Действия пустоты — вторичные;
                  дубль «Создать проект» отсюда снят. */}
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="sm"
                  variant="secondary"
                  // F4 — единый setSearch (раньше 5 вызовов подряд из одного замыкания
                  // перетирали друг друга — чистился только последний; скептик #15).
                  // visibility тоже сбрасывается.
                  onClick={() =>
                    setSearch({
                      stage: undefined,
                      health: undefined,
                      q: undefined,
                      customer_id: undefined,
                      entity_id: undefined,
                      visibility: undefined,
                      // F5 — сбрасываем и rule-builder параметры (исключения / срок / пусто).
                      stage_not: undefined,
                      health_not: undefined,
                      expected_end_from: undefined,
                      expected_end_to: undefined,
                      end_date_empty: undefined,
                    })
                  }
                >
                  <FilterX size={14} />{' '}
                  {searchOnly ? m.project_search_clear() : m.project_filter_reset()}
                </Button>
                {/* F15 круг 2 (UX, MINOR): поиск без архивных может не найти
                    архивный проект, и пустота об этом молчала. Действие расширяет
                    охват, не сбрасывая запрос. */}
                {q && !includeArchived && (
                  <Button size="sm" variant="secondary" onClick={() => setIncludeArchived(true)}>
                    {m.project_search_include_archived()}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <EmptyState title={m.project_title_list()} description={m.project_cta_create()} />
          )
        ) : groupByCompany && showEntityColumn ? (
          // projects-by-company P4 — секции по компании: заголовок (имя + count) + список.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32, marginTop: 8 }}>
            {groupedByEntity.map(([eid, list]) => (
              <section key={eid}>
                {/* design-review (UI/UX): section-band — заметная полоса-якорь, вес заголовка
                  отличает границу компаний от строк таблицы (иерархия весом, Calm Industrial). */}
                <h3
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '0 0 12px',
                    padding: '8px 12px',
                    borderRadius: 8,
                    background: 'var(--bg-surface)',
                    borderLeft: '3px solid var(--accent)',
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--text-heading, var(--text-primary))',
                  }}
                >
                  <Building2 size={16} aria-hidden="true" style={{ color: 'var(--text-muted)' }} />
                  {entityName(eid)}
                  <span
                    style={{
                      marginLeft: 'auto',
                      color: 'var(--text-muted)',
                      fontWeight: 400,
                      fontSize: 13,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {list.length}
                  </span>
                </h3>
                {renderList(list)}
              </section>
            ))}
          </div>
        ) : (
          renderList(rows)
        )}
      </div>
    </WorkspaceSurface>
  );
}

/** FE-135 (Sprint 12) — chip для granular снятия одного active фильтра. */
function FilterChip(props: { label: string; onClear: () => void }): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 8px',
        borderRadius: 12,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        color: 'var(--text-primary)',
      }}
    >
      {props.label}
      <button
        type="button"
        onClick={props.onClear}
        aria-label={`Снять фильтр ${props.label}`}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          color: 'var(--text-muted)',
          display: 'inline-flex',
          lineHeight: 0,
        }}
      >
        <X size={12} />
      </button>
    </span>
  );
}
